import { cn } from "@/lib/utils";
import { Drawer } from "vaul";

/**
 * The phone sheet (iOS `.sheet`): every dialog and sheet presents as this on
 * phones, so they all share one look and one gesture. Rounded top with a grab
 * handle, slides up from the bottom, and dragging anywhere on it moves it.
 *
 * The gesture is vaul's (a Radix Dialog underneath, so `DialogPrimitive.Title`
 * / `Close` work inside): velocity-aware release, rubber band, detents
 * (`snapPoints`), and content that scrolls until it is back at the top before
 * the sheet starts to follow the finger. A hand-rolled `useDrag` version
 * closed on releases meant to settle and only dragged from the header.
 */
export const BottomSheet = Drawer.Root;

/**
 * Classes that make any content a phone sheet. Applied after the caller's
 * className so a desktop size or position (max-w-xs, inset-0, rounded-none…)
 * can't leak in, while padding, flex and colours still come through.
 * Motion comes from vaul's own stylesheet, so no animate-in classes here.
 */
const SHEET_CLASSES = [
  "fixed inset-x-0 bottom-0 top-auto left-0 right-0 z-[200] w-full max-w-none translate-x-0 translate-y-0",
  "max-h-[calc(100dvh-max(12px,env(safe-area-inset-top)))] overflow-y-auto overscroll-contain",
  "flex flex-col rounded-t-[28px] rounded-b-none border-x-0 border-b-0 border-t border-border",
  "pb-[env(safe-area-inset-bottom)] font-chrome outline-none focus-visible:shadow-none",
  "shadow-[0_-8px_30px_var(--color-shadow)]",
  // vaul's filler under the sheet (`::after`, 200% tall) is overflow of this
  // scroller, so it became blank space to scroll into.
  "after:hidden",
].join(" ");

/**
 * vaul pins the sheet's height in pixels while the keyboard is up, and back to
 * its pre-keyboard height once it goes down, so content that grows afterwards
 * (a new invitee in the share sheet) scrolls inside a sheet that stays put.
 * Keep vaul's number as a cap instead (the room above the keyboard), and drop
 * it once the keyboard is down (vaul puts `bottom` back to 0): the sheet then
 * fits its content again, up to the class's max height.
 */
export function fitSheetToContent(el: HTMLElement | null) {
  if (!el) return;
  const fit = () => {
    const { style } = el;
    const lifted = !!style.bottom && style.bottom !== "0px";
    const cap = lifted ? style.height || style.maxHeight : "";
    if (style.height) style.height = "";
    if (style.maxHeight !== cap) style.maxHeight = cap;
  };
  // No disconnect: vaul's composed ref drops a ref cleanup, and the observer
  // goes with the element when the sheet unmounts.
  new MutationObserver(fit).observe(el, { attributes: true, attributeFilter: ["style"] });
}

/**
 * A tap on a toast is not a tap outside the dialog: toasts take pointer
 * events over modal layers (index.css), and dismissing a toast must not
 * close the sheet or dialog under it.
 */
export function ignoreToastInteraction(e: { target: EventTarget | null; preventDefault(): void }) {
  if (e.target instanceof Element && e.target.closest("[data-sonner-toaster]")) e.preventDefault();
}

export function BottomSheetContent({
  className,
  children,
  onCloseAutoFocus,
  onInteractOutside,
  onPointerDownOutside,
  ...props
}: React.ComponentProps<typeof Drawer.Content>) {
  return (
    <Drawer.Portal>
      <Drawer.Overlay className="fixed inset-0 z-[200] bg-overlay" />
      <Drawer.Content
        ref={fitSheetToContent}
        aria-describedby={undefined}
        onCloseAutoFocus={(e) => {
          e.preventDefault();
          onCloseAutoFocus?.(e);
        }}
        onPointerDownOutside={(e) => {
          ignoreToastInteraction(e);
          onPointerDownOutside?.(e);
        }}
        onInteractOutside={(e) => {
          ignoreToastInteraction(e);
          onInteractOutside?.(e);
        }}
        className={cn("bg-canvas", className, SHEET_CLASSES)}
        {...props}
      >
        {/* Drawn over the top edge, taking no height: the title row sits as
            close to the top as its close button sits to the right. */}
        <span
          aria-hidden
          className="pointer-events-none absolute left-1/2 top-1.5 h-[5px] w-9 -translate-x-1/2 rounded-full bg-border"
        />
        {children}
      </Drawer.Content>
    </Drawer.Portal>
  );
}
